import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, IconButton, Collapse, Tooltip, alpha, Badge, Menu, MenuItem, TextField, ListSubheader, Chip, Checkbox, ListItemButton } from '@mui/material';
import { KeyboardArrowDown as ChevronDownIcon, KeyboardArrowRight as ChevronRightIcon } from '@mui/icons-material';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import UserMenu from './UserMenu';
import CustomAvatar from '@ui/components/common/Avatar';
import { Icon } from '@iconify/react';
import { useHelpTopicContext } from '@ui/contexts';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { logout, startRolePreview, stopRolePreview } from '@application/features/auth/authSlice';
import { ROLE_PREVIEW_OPTIONS } from '@application/constants/role-preview-permissions';
import LocalStorageService from '@application/common/local-storage-service';
import { SubMenuIcon } from '@ui/assets/custom-icons/submenu-icon';
import EventMenu from './eventMenu';
import { useSelector } from 'react-redux';
import { setSelectedCompanyId } from '@application/features/company/companySlice';
import { refreshTokenAsync } from '@application/features/auth/authThunk';
import { getAllPeopleForTasksAsync } from '@application/features/people/peopleThunk';
import { NotificationBing, SearchNormal } from 'iconsax-react';
import StyledSearchInput from '../StyledSearchInput';
import { getContrastText } from '@ui/theme/palette';
import { getLatestUsageManualAsync } from '@application/features/usageManual/usageManualThunk';
import { useTranslation } from 'react-i18next';
const filterNavigationItems = (items, userPermissions = []) => {
    return items
        .map((item) => {
        if (item.submenuItems && item.submenuItems.length > 0) {
            const filteredSubmenu = filterNavigationItems(item.submenuItems, userPermissions);
            return {
                ...item,
                submenuItems: filteredSubmenu,
                hasSubmenu: filteredSubmenu.length > 0
            };
        }
        return item;
    })
        .filter((item) => {
        if (item.permission && !userPermissions.includes(item.permission)) {
            return false;
        }
        // If the item has a submenu array but it's empty, we must hide the parent
        if (item.submenuItems && item.submenuItems.length === 0) {
            return false;
        }
        return true;
    });
};
const Sidebar = ({ logo = true, logoText = 'Happiness', user, searchPlaceholder, onSearch, searchValue, navigationSections, onNavigationItemClick, open = true, width = 320, collapsedWidth = 88, backgroundColor, textColor, activeItemColor, activeItemBackgroundColor, className, sx }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'xl'));
    const navigate = useNavigate();
    const location = useLocation();
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [isCollapsed, setisCollapsed] = useState(true);
    const [submenuItems, setSubmenuItems] = useState(null);
    const [eventMenuAnchor, setEventMenuAnchor] = useState(null);
    const [companyMenuAnchor, setCompanyMenuAnchor] = useState(null);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [expandedRegions, setExpandedRegions] = useState(new Set());
    const [collapsedRegionsDuringSearch, setCollapsedRegionsDuringSearch] = useState(new Set());
    const normalizedCompanySearchTerm = useMemo(() => companySearchTerm.trim().toLowerCase(), [companySearchTerm]);
    const [debouncedCompanySearchTerm, setDebouncedCompanySearchTerm] = useState(normalizedCompanySearchTerm);
    const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
    const selectedUser = useSelector((state) => state.auth.currentUser);
    const notificationsBadgeCount = useSelector((state) => state.notifications.badgeCount);
    const { openHelpDrawer } = useHelpTopicContext();
    const companies = useSelector((state) => state.company.companies);
    const companiesByRegion = useSelector((state) => state.company.companiesByRegion);
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    const dispatch = useAppDispatch();
    const companyTheme = useAppSelector((state) => state.companyTheme.currentTheme);
    const isPreviewMode = useSelector((state) => state.auth.isPreviewMode);
    const previewRoleLabel = useSelector((state) => state.auth.previewRoleLabel);
    const originalCurrentUser = useSelector((state) => state.auth.originalCurrentUser);
    const latestManual = useSelector((state) => state.usageManual.latest);
    const [previewMenuAnchor, setPreviewMenuAnchor] = useState(null);
    const realUserRoles = isPreviewMode ? originalCurrentUser?.role : selectedUser?.role;
    const isHappinessManager = realUserRoles?.includes('HappinessManager') ?? false;
    const filteredNavigationSections = useMemo(() => {
        const userPermissions = selectedUser?.permissions || [];
        return navigationSections
            .map((section) => ({
            ...section,
            items: filterNavigationItems(section.items, userPermissions)
        }))
            .filter((section) => section.items.length > 0);
    }, [navigationSections, selectedUser?.permissions]);
    // Track active item ID
    const [activeItemId, setActiveItemId] = useState(null);
    // Find the active item based on current path
    useEffect(() => {
        // Flatten all navigation items
        const allItems = [];
        filteredNavigationSections.forEach((section) => {
            section.items.forEach((item) => {
                allItems.push(item);
                if (item.submenuItems) {
                    allItems.push(...item.submenuItems);
                }
            });
        });
        // Find the item that matches the current path
        const activeItem = allItems.find((item) => {
            if (item.href) {
                // Special case: Match /events (All Events) when on event-details or create-event with eventId
                if (item.href === '/events') {
                    if (location.pathname.startsWith('/event-details/')) {
                        return true;
                    }
                    if (location.pathname === '/create-event' && location.search.includes('eventId=')) {
                        return true;
                    }
                }
                if (item.href === '/people') {
                    if (location.pathname.startsWith('/people/')) {
                        return true;
                    }
                }
                if (item.href === '/relations') {
                    if (location.pathname.startsWith('/relations/')) {
                        return true;
                    }
                }
                if (item.href === '/sponsorships') {
                    if (location.pathname.startsWith('/sponsorship-details/')) {
                        return true;
                    }
                }
                // Use matchPath for more flexible matching (supports dynamic routes)
                const pathMatches = matchPath({ path: item.href, end: true }, location.pathname);
                // Special case: Don't match /create-event when eventId query param exists
                if (item.href === '/create-event' && location.search.includes('eventId=')) {
                    return false;
                }
                return pathMatches;
            }
            return false;
        });
        setActiveItemId(activeItem?.id || null);
        // If this item has a parent with submenu, expand the parent
        if (activeItem) {
            const parentItem = allItems.find((item) => item.submenuItems?.some((sub) => sub.id === activeItem.id));
            if (parentItem) {
                setExpandedItems((prev) => new Set(prev).add(parentItem.id));
            }
        }
    }, [location.pathname, filteredNavigationSections]);
    useEffect(() => {
        if (companies.length === 0)
            return;
        if (selectedCompanyId && selectedCompanyId.length > 0)
            return;
        const storedIds = LocalStorageService.getSelectedCompanyIdForTab() || [];
        const validStoredIds = storedIds.filter((id) => companies.some((c) => c.id === id));
        if (validStoredIds.length > 0) {
            dispatch(setSelectedCompanyId(validStoredIds));
        }
        else {
            const primaryCompany = selectedUser?.primaryCompanyId
                ? companies.find((c) => c.id === selectedUser.primaryCompanyId)
                : null;
            const defaultSelection = primaryCompany ? [primaryCompany.id] : [companies[0].id];
            dispatch(setSelectedCompanyId(defaultSelection));
            LocalStorageService.setSelectedCompanyIdForTab(defaultSelection);
        }
    }, [companies, selectedCompanyId, selectedUser, dispatch]);
    // Cross-tab logout broadcast: if another tab logs out, clear this tab's selected company so next login defaults to primary.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === 'LOGOUT_BROADCAST') {
                LocalStorageService.removeSelectedCompanyIdForTab();
                dispatch(setSelectedCompanyId([]));
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [dispatch]);
    useEffect(() => {
        if (!latestManual) {
            dispatch(getLatestUsageManualAsync());
        }
    }, [dispatch, latestManual]);
    // open handler
    const onOpenEventMenu = (event) => {
        setEventMenuAnchor(event.currentTarget);
    };
    // close handler
    const onCloseEventMenu = () => {
        setEventMenuAnchor(null);
    };
    const getCompanyLogoUrl = () => {
        // If we're showing the primary company (hard refresh scenario)
        if (selectedUser?.primaryCompanyId &&
            selectedCompanyId?.length === 1 &&
            selectedCompanyId[0] === selectedUser?.primaryCompanyId) {
            return selectedUser?.primaryCompanyLogoUrl;
        }
        return selectedUser?.companyLogoUrl;
    };
    const handleLogout = () => {
        // Soft application logout: clear app tokens and mark MSAL logout to prevent auto-login
        dispatch(logout());
        LocalStorageService.removeAccessToken();
        LocalStorageService.removeMSAccessToken();
        // Clear per-tab company selection and notify other tabs to clear theirs too.
        LocalStorageService.removeSelectedCompanyIdForTab();
        LocalStorageService.broadcastLogout();
        // Mark that user intentionally logged out so redirect handling ignores any MSAL state
        try {
            sessionStorage.setItem('msal_logout', 'true');
            // Also clear any pending login initiation flag
            sessionStorage.removeItem('msal_login_initiated');
        }
        catch (e) {
            // ignore storage errors
        }
        // Navigate to login page (soft logout - keeps Microsoft session)
        navigate('/login');
    };
    const handleNavigationItemClick = (item) => {
        // Set this item as active
        setActiveItemId(item.id);
        if (onNavigationItemClick) {
            onNavigationItemClick(item);
        }
        if (item.onClick) {
            item.onClick();
        }
        // Handle navigation
        if (item.href) {
            navigate(item.href);
        }
    };
    const handleSubmenuToggle = (itemId) => {
        const newExpandedItems = new Set(expandedItems);
        if (newExpandedItems.has(itemId)) {
            newExpandedItems.delete(itemId);
        }
        else {
            newExpandedItems.add(itemId);
        }
        setExpandedItems(newExpandedItems);
    };
    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };
    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };
    const onAllEventsClick = () => {
        navigate('/events');
    };
    const onCreateEventsClick = () => {
        navigate('/create-event');
    };
    const handleMyAccount = () => { };
    const handleLanguageChange = () => { };
    const handleGroupChange = () => { };
    const onCompanySelect = (companyId) => {
        const updatedSelection = selectedCompanyId.includes(companyId)
            ? selectedCompanyId.filter((id) => id !== companyId)
            : [...selectedCompanyId, companyId];
        // Update Redux state
        dispatch(setSelectedCompanyId(updatedSelection));
        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    const onRegionToggle = (companyIds) => {
        if (!companyIds.length)
            return;
        const allSelected = companyIds.every((id) => selectedCompanyId.includes(id));
        let updatedSelection;
        if (allSelected) {
            // Deselect all companies in this region
            updatedSelection = selectedCompanyId.filter((id) => !companyIds.includes(id));
        }
        else {
            // Select all companies in this region (merge + de-duplicate)
            const merged = [...selectedCompanyId, ...companyIds];
            updatedSelection = Array.from(new Set(merged));
        }
        dispatch(setSelectedCompanyId(updatedSelection));
        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    const toggleRegionExpand = (regionKey) => {
        const newExpandedRegions = new Set(expandedRegions);
        const hasSearch = companySearchTerm.trim().length > 0;
        if (hasSearch) {
            setCollapsedRegionsDuringSearch((prev) => {
                const next = new Set(prev);
                const isAutoOpen = !next.has(regionKey);
                if (isAutoOpen)
                    next.add(regionKey);
                else
                    next.delete(regionKey);
                return next;
            });
            return;
        }
        if (newExpandedRegions.has(regionKey)) {
            newExpandedRegions.delete(regionKey);
        }
        else {
            newExpandedRegions.clear();
            newExpandedRegions.add(regionKey);
        }
        setExpandedRegions(newExpandedRegions);
    };
    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedCompanySearchTerm(normalizedCompanySearchTerm);
        }, 250);
        return () => window.clearTimeout(handle);
    }, [normalizedCompanySearchTerm]);
    useEffect(() => {
        setCollapsedRegionsDuringSearch(new Set());
    }, [debouncedCompanySearchTerm]);
    const filteredCompaniesForMenu = useMemo(() => {
        const term = companySearchTerm.trim().toLowerCase();
        if (!term)
            return companies;
        return companies.filter((c) => c.name.toLowerCase().includes(term));
    }, [companies, companySearchTerm]);
    const handleSelectAllCompanies = () => {
        const allCompanyIds = companies.map((c) => c.id);
        dispatch(setSelectedCompanyId(allCompanyIds));
        LocalStorageService.setSelectedCompanyIdForTab(allCompanyIds);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    const handleDeselectAllCompanies = () => {
        dispatch(setSelectedCompanyId([]));
        LocalStorageService.setSelectedCompanyIdForTab([]);
        dispatch(refreshTokenAsync());
        dispatch(getAllPeopleForTasksAsync());
    };
    // Filter regions by search; render by region with selected first then unselected per region
    const renderCompanyMenuItems = () => {
        const filteredRegions = companiesByRegion
            .map((region) => ({
            ...region,
            companies: region.companies.filter((c) => c.name.toLowerCase().includes(companySearchTerm.toLowerCase()))
        }))
            .filter((r) => r.companies.length > 0);
        return filteredRegions.map((region) => {
            const regionCompanyIds = region.companies.map((c) => c.id);
            const selectedInRegion = region.companies
                .filter((c) => selectedCompanyId.includes(c.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            const unselectedInRegion = region.companies
                .filter((c) => !selectedCompanyId.includes(c.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            const allSelected = regionCompanyIds.length > 0 &&
                regionCompanyIds.every((id) => selectedCompanyId.includes(id));
            const someSelected = !allSelected && regionCompanyIds.some((id) => selectedCompanyId.includes(id));
            return (_jsxs(Box, { sx: { flexShrink: 0 }, children: [_jsxs(ListItem, { sx: {
                            ...theme.typography.caption,
                            color: theme.palette.common.grey.dark,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            pr: 1,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.common.grey.main, 0.05)
                            }
                        }, onClick: (e) => {
                            // Prevent menu from closing when toggling region expand
                            e.stopPropagation();
                            toggleRegionExpand(region.regionKey);
                        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(ChevronRightIcon, { sx: {
                                            fontSize: 16,
                                            transition: theme.transitions.create('transform', {
                                                duration: theme.transitions.duration.short
                                            }),
                                            transform: expandedRegions.has(region.regionKey) ||
                                                (companySearchTerm.trim().length > 0 &&
                                                    !collapsedRegionsDuringSearch.has(region.regionKey))
                                                ? 'rotate(90deg)'
                                                : 'rotate(0deg)'
                                        } }), region.regionLabel] }), _jsx(Checkbox, { size: 'small', checked: allSelected, indeterminate: someSelected, onClick: (e) => {
                                    e.stopPropagation();
                                    onRegionToggle(regionCompanyIds);
                                } })] }), _jsx(Collapse, { in: expandedRegions.has(region.regionKey) ||
                            (companySearchTerm.trim().length > 0 &&
                                !collapsedRegionsDuringSearch.has(region.regionKey)), timeout: 'auto', unmountOnExit: true, sx: { display: 'block', width: '100%' }, children: _jsxs(Box, { sx: { width: '100%', overflowY: 'auto', maxHeight: '300px', scrollbarGutter: 'stable' }, children: [selectedInRegion.map((company) => (_jsxs(MenuItem, { selected: true, onClick: () => onCompanySelect(company.id), sx: {
                                        ...theme.typography.body2,
                                        color: theme.palette.text.primary,
                                        fontSize: '14px',
                                        pl: 3,
                                        minHeight: '48px',
                                        '&:hover': { backgroundColor: theme.palette.grey[50] }
                                    }, children: [_jsx(Checkbox, { checked: true, size: 'medium', sx: { mr: 1 } }), _jsx(ListItemText, { primary: company.name, sx: { minWidth: 0 }, primaryTypographyProps: {
                                                sx: {
                                                    ...theme.typography.body2,
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            } })] }, company.id))), unselectedInRegion.map((company) => (_jsxs(MenuItem, { selected: false, onClick: () => onCompanySelect(company.id), sx: {
                                        ...theme.typography.body2,
                                        fontSize: '14px',
                                        color: theme.palette.text.primary,
                                        pl: 3,
                                        '&:hover': { backgroundColor: theme.palette.grey[50] }
                                    }, children: [_jsx(Checkbox, { checked: false, size: 'medium', sx: { mr: 1 } }), _jsx(ListItemText, { primary: company.name, sx: { minWidth: 0 }, primaryTypographyProps: {
                                                sx: {
                                                    ...theme.typography.body2,
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }
                                            } })] }, company.id)))] }) })] }, region.regionKey));
        });
    };
    const toggleSidebar = () => {
        setisCollapsed((prev) => !prev);
    };
    const isItemActive = (item) => {
        // Use the activeItemId state to determine if this item is active
        return activeItemId === item.id;
    };
    const hasActiveSubmenu = (item) => {
        return item.submenuItems?.some((sub) => isItemActive(sub)) || false;
    };
    // Display selected companies - show count if multiple, or name if single
    const selectedCompany = useMemo(() => {
        if (selectedCompanyId.length === 0)
            return '';
        if (selectedCompanyId.length === 1) {
            return companies.find((c) => c.id === selectedCompanyId[0])?.name || '';
        }
        return `${selectedCompanyId.length} ${t('common.companiesSelected') || 'companies selected'}`;
    }, [selectedCompanyId, companies, t]);
    const renderNavigationItem = (item, level = 0) => {
        const isActive = isItemActive(item);
        const activeSubmenu = hasActiveSubmenu(item);
        const parentIsActive = isActive || activeSubmenu;
        const hasSubmenu = item.hasSubmenu && item.submenuItems && item.submenuItems.length > 0;
        return (_jsxs(React.Fragment, { children: [_jsx(Tooltip, { title: !isCollapsed ? item.label : '', placement: 'right', arrow: true, children: _jsxs(ListItem, { onClick: (e) => {
                            if (hasSubmenu) {
                                if (!isCollapsed) {
                                    // When collapsed, open event menu
                                    onOpenEventMenu(e);
                                }
                                else {
                                    // When expanded, toggle submenu
                                    handleSubmenuToggle(item.id);
                                }
                            }
                            else {
                                handleNavigationItemClick(item);
                            }
                        }, sx: {
                            borderRadius: theme.spacing(1),
                            cursor: 'pointer',
                            minWidth: 0,
                            height: '44px',
                            paddingLeft: !isCollapsed ? 0 : theme.spacing(1 + level * 2),
                            paddingRight: !isCollapsed ? 0 : theme.spacing(1.5),
                            backgroundColor: parentIsActive
                                ? alpha(theme.palette.common.white, 0.08)
                                : 'transparent',
                            border: parentIsActive
                                ? `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                                : 'none',
                            '&:hover': {
                                backgroundColor: parentIsActive
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.common.white, 0.08)
                            },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: !isCollapsed ? 'center' : 'flex-start',
                            gap: !isCollapsed ? 0.5 : 1,
                            width: '100%',
                            my: '4px',
                            position: 'relative' // Add relative positioning for absolute child
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: 1,
                                    minWidth: 0,
                                    flexDirection: !isCollapsed ? 'column' : 'row',
                                    gap: !isCollapsed ? 0.5 : 1
                                }, children: [item.icon && (_jsx(ListItemIcon, { sx: {
                                            minWidth: 0,
                                            justifyContent: 'center',
                                            color: parentIsActive
                                                ? activeItemColor || theme.palette.common.white
                                                : textColor || theme.palette.grey[400]
                                        }, children: React.isValidElement(item.icon) ? (item.icon) : typeof item.icon === 'string' ? (_jsx("img", { src: item.icon, alt: item.label, style: { width: 24, height: 24 } })) : typeof item.icon === 'function' ? (React.createElement(item.icon)) : null })), isCollapsed && (_jsx(ListItemText, { primary: item.label, primaryTypographyProps: {
                                            noWrap: true,
                                            sx: {
                                                flex: 1,
                                                minWidth: 0,
                                                width: '100%',
                                                maxWidth: '100%',
                                                fontSize: !isCollapsed ? '10px' : '14px',
                                                color: parentIsActive
                                                    ? activeItemColor || theme.palette.common.white
                                                    : textColor || theme.palette.grey[400],
                                                fontWeight: parentIsActive ? 600 : 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'block'
                                            }
                                        }, sx: {
                                            textAlign: !isCollapsed ? 'center' : 'left',
                                            minWidth: 0,
                                            flex: 1,
                                            width: '100%'
                                        } }))] }), hasSubmenu && (_jsx(IconButton, { size: 'small', sx: {
                                    border: 'none',
                                    padding: 0,
                                    width: 24,
                                    height: 24,
                                    minWidth: 24,
                                    flexShrink: 0,
                                    // Position absolute when collapsed, normal when expanded
                                    position: !isCollapsed ? 'absolute' : 'relative',
                                    right: !isCollapsed ? theme.spacing(0.5) : 'auto',
                                    top: !isCollapsed ? 7.5 : 'auto',
                                    // transform: !isCollapsed ? 'translateY(-40%)' : 'none',
                                    zIndex: !isCollapsed ? 1 : 'auto'
                                }, children: _jsx(ChevronRightIcon, { sx: {
                                        fontSize: 16,
                                        color: parentIsActive
                                            ? activeItemColor || theme.palette.common.white
                                            : textColor || theme.palette.grey[400],
                                        transform: expandedItems.has(item.id) && isCollapsed ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: theme.transitions.create('transform', {
                                            duration: theme.transitions.duration.short
                                        })
                                    } }) }))] }) }), hasSubmenu && isCollapsed && (_jsx(Collapse, { in: expandedItems.has(item.id), className: 'collapsed', timeout: 'auto', unmountOnExit: true, children: _jsx(Box, { sx: {
                            paddingLeft: theme.spacing(4),
                            display: 'flex',
                            flexDirection: 'column'
                        }, children: item.submenuItems?.map((subItem) => {
                            const isSubActive = isItemActive(subItem);
                            return (_jsxs(ListItem, { onClick: () => handleNavigationItemClick(subItem), sx: {
                                    marginTop: isSubActive ? 0.5 : undefined,
                                    height: '36px',
                                    backgroundColor: isSubActive
                                        ? alpha(theme.palette.common.white, 0.08)
                                        : 'transparent',
                                    border: isSubActive
                                        ? `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                                        : 'none',
                                    '&:hover': {
                                        backgroundColor: isSubActive
                                            ? alpha(theme.palette.common.white, 0.08)
                                            : alpha(theme.palette.common.white, 0.08)
                                    },
                                    borderRadius: theme.spacing(1),
                                    cursor: 'pointer',
                                    paddingLeft: theme.spacing(1),
                                    paddingRight: !isCollapsed ? 0 : theme.spacing(1.5),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: !isCollapsed ? 'center' : 'flex-start',
                                    gap: 2,
                                    width: '100%'
                                }, children: [_jsx(ListItemIcon, { sx: { minWidth: 0, justifyContent: 'center' }, children: _jsx(SubMenuIcon, { dotColor: isSubActive
                                                ? activeItemColor || theme.palette.common.white
                                                : textColor || theme.palette.grey[400] }) }), isCollapsed && (_jsx(ListItemText, { primary: subItem.label, sx: {
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                color: textColor || isSubActive
                                                    ? theme.palette.common.white
                                                    : theme.palette.grey[400],
                                                fontWeight: isSubActive ? 600 : 500
                                            }
                                        } }))] }, subItem.id));
                        }) }) }))] }, item.id));
    };
    // Don't render sidebar on mobile and tablet
    if (isMobile || isTablet) {
        return null;
    }
    // When multiple companies are selected, use VDK Groep theme
    const isMultipleCompaniesSelected = selectedCompanyId.length > 1;
    const isVDKGroep = isMultipleCompaniesSelected ||
        selectedCompany?.toLowerCase().includes('vdk groep') ||
        selectedCompany?.toLowerCase().includes('vdkgroep');
    // Check if company has no customizations at all
    const hasNoCustomizations = !companyTheme?.primaryColor && !companyTheme?.secondaryColor && !companyTheme?.fontFamily;
    // Apply VDK gradient if: VDK groep OR no customizations at all
    const shouldApplyVDKGradient = isVDKGroep || hasNoCustomizations;
    const sidebarSx = {
        width: isCollapsed ? width : collapsedWidth,
        flexShrink: 0,
        color: textColor || theme.palette.grey[400],
        // borderRadius: theme.spacing(4),
        height: '100vh',
        px: 2,
        borderRight: shouldApplyVDKGradient ? `1px solid #3192D0` : '1px solid black',
        overflow: 'visible',
        position: 'relative',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
        }),
        ...sx
    };
    return (_jsxs(Box, { className: className, sx: sidebarSx, children: [_jsxs(Box, { sx: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: !isCollapsed ? 'center' : 'flex-start',
                    padding: isCollapsed ? theme.spacing(3, 0, 1, 0) : theme.spacing(3, 0.75, 0.75, 0.75)
                }, children: [_jsx(IconButton, { onClick: toggleSidebar, size: 'small', sx: {
                            position: 'absolute',
                            right: -18,
                            top: 42,
                            zIndex: 100,
                            borderRadius: '500px',
                            backdropFilter: 'blur(40px)',
                            backgroundColor: isVDKGroep ? alpha(theme.palette.primary.dark, 0.2) : 'transparent',
                            color: textColor || theme.palette.common.white,
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                            '&:hover': {
                                backgroundColor: isVDKGroep ? alpha(theme.palette.primary.dark, 0.2) : 'transparent'
                            }
                        }, children: isCollapsed ? (_jsx(Icon, { icon: 'eva:arrow-ios-back-outline', width: 20, height: 20 })) : (_jsx(Icon, { icon: 'eva:arrow-ios-forward-outline', width: 20, height: 20 })) }), _jsxs(Box, { onClick: (e) => setCompanyMenuAnchor(e.currentTarget), sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: theme.spacing(1.5),
                            cursor: 'pointer',
                            paddingRight: theme.spacing(2),
                            transition: 'all 0.2s'
                        }, children: [_jsx(Box, { sx: {
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.common.white,
                                    padding: '4px',
                                    flexShrink: 0
                                }, children: _jsx(Box, { component: 'img', src: getCompanyLogoUrl() || '/assets/images/Logo.png', alt: 'Company Logo', sx: {
                                        width: 32,
                                        height: 32,
                                        objectFit: 'contain'
                                    } }) }), isCollapsed && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'body2', color: theme.palette.common.white, sx: {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            flex: 1
                                        }, children: selectedCompany || 'VDK Groep' }), _jsx(ChevronDownIcon, { sx: {
                                            fontSize: '12px',
                                            color: theme.palette.grey[400],
                                            transition: 'transform 0.2s',
                                            transform: Boolean(companyMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)'
                                        } })] }))] }), user && (_jsx(Box, { sx: {
                            margin: theme.spacing(!isCollapsed ? 0 : 1, 0, 1, 0),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isCollapsed ? 'flex-start' : 'center',
                            gap: theme.spacing(1.5),
                            pt: theme.spacing(2),
                            position: 'relative'
                        }, children: _jsxs(Box, { onClick: handleUserMenuOpen, sx: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: theme.spacing(1.5),
                                cursor: 'pointer',
                                flex: 1,
                                minWidth: 0
                            }, children: [!isCollapsed && (_jsx(CustomAvatar, { src: selectedUser?.ProfilePicturePath, name: selectedUser?.name || '' })), isCollapsed && (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0, gap: 0.5 }, children: [_jsx(Typography, { variant: 'body1', sx: {
                                                        color: textColor || theme.palette.common.white,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }, children: selectedUser?.name }), _jsx(Typography, { variant: 'body2', sx: {
                                                        color: theme.palette.grey[400],
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }, children: (selectedUser?.role ?? []).map((r) => r === 'TransportManager'
                                                        ? t('collaborators.roleTransportManager')
                                                        : r === 'Collaborator'
                                                            ? t('collaborators.roleCollaborator')
                                                            : r).join(', ') })] }), _jsx(ChevronDownIcon, { sx: {
                                                color: textColor || theme.palette.grey[400],
                                                fontSize: '12px',
                                                transition: theme.transitions.create('transform', {
                                                    duration: theme.transitions.duration.short
                                                }),
                                                transform: Boolean(userMenuAnchor) ? 'rotate(180deg)' : 'rotate(0deg)'
                                            } })] }))] }) })), isHappinessManager && isCollapsed && (_jsxs(Box, { sx: { mb: 1 }, children: [isPreviewMode ? (_jsx(Chip, { label: t('rolePreview.previewingChip', { role: previewRoleLabel ? t(previewRoleLabel) : '' }), size: 'small', icon: _jsx(Icon, { icon: 'solar:eye-bold', width: 14 }), onDelete: () => { dispatch(stopRolePreview()); navigate('/'); }, sx: {
                                    width: '100%',
                                    backgroundColor: alpha(theme.palette.warning.main, 0.2),
                                    color: theme.palette.warning.light,
                                    '& .MuiChip-icon': { color: theme.palette.warning.light },
                                    '& .MuiChip-deleteIcon': { color: theme.palette.warning.light },
                                    mb: 0.5
                                } })) : (_jsxs(ListItemButton, { onClick: (e) => setPreviewMenuAnchor(e.currentTarget), sx: {
                                    borderRadius: theme.spacing(1),
                                    backgroundColor: alpha(theme.palette.common.white, 0.05),
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                                    '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.1) },
                                    py: 0.5,
                                    px: 1,
                                    mb: 0.5
                                }, children: [_jsx(ListItemIcon, { sx: { minWidth: 28, color: textColor || theme.palette.grey[400] }, children: _jsx(Icon, { icon: 'solar:eye-bold', width: 16 }) }), _jsx(ListItemText, { primary: t('rolePreview.previewAsRole'), primaryTypographyProps: {
                                            variant: 'caption',
                                            color: textColor || theme.palette.grey[400]
                                        } }), _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', width: 14, color: theme.palette.grey[500] })] })), _jsx(Menu, { anchorEl: previewMenuAnchor, open: Boolean(previewMenuAnchor), onClose: () => setPreviewMenuAnchor(null), anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { sx: { minWidth: 220, borderRadius: 2, mt: 0.5 } }, children: ROLE_PREVIEW_OPTIONS.map((option) => (_jsx(MenuItem, { onClick: () => {
                                        dispatch(startRolePreview(option));
                                        setPreviewMenuAnchor(null);
                                        navigate('/');
                                    }, children: t(option.label) }, option.key))) })] })), _jsx(ListItem, { onClick: openHelpDrawer, sx: {
                            borderRadius: theme.spacing(1),
                            cursor: 'pointer',
                            minWidth: 0,
                            mb: 1,
                            paddingLeft: !isCollapsed ? 0 : theme.spacing(1),
                            paddingRight: !isCollapsed ? 0 : theme.spacing(2),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: !isCollapsed ? 'center' : 'flex-start',
                            gap: !isCollapsed ? 0.5 : 1,
                            width: '100%',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.common.white, 0.08)
                            }
                        }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                minWidth: 0,
                                flexDirection: !isCollapsed ? 'column' : 'row',
                                gap: !isCollapsed ? 0.5 : 1
                            }, children: [_jsx(ListItemIcon, { sx: {
                                        minWidth: 0,
                                        justifyContent: 'center',
                                        color: textColor || theme.palette.grey[400]
                                    }, children: _jsx(Icon, { icon: 'ci:circle-help', width: 24, height: 24 }) }), isCollapsed && (_jsx(ListItemText, { primary: t('helpCenter.buttonLabel') || 'Help', primaryTypographyProps: {
                                        noWrap: true,
                                        sx: {
                                            flex: 1,
                                            minWidth: 0,
                                            width: '100%',
                                            maxWidth: '100%',
                                            fontSize: !isCollapsed ? '10px' : '14px',
                                            color: textColor || theme.palette.grey[400],
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block'
                                        }
                                    }, sx: {
                                        minWidth: 0,
                                        flex: 1,
                                        width: '100%'
                                    } }))] }) }), !selectedUser?.role?.some((r) => r === 'Supplier' || r === 'TransportManager') && _jsx(ListItem, { onClick: () => navigate('/notifications'), sx: {
                            borderRadius: theme.spacing(1),
                            cursor: 'pointer',
                            minWidth: 0,
                            mb: 2,
                            paddingLeft: !isCollapsed ? 0 : theme.spacing(1),
                            paddingRight: !isCollapsed ? 0 : theme.spacing(2),
                            backgroundColor: location.pathname === '/notifications'
                                ? alpha(theme.palette.common.white, 0.08)
                                : 'transparent',
                            border: location.pathname === '/notifications'
                                ? `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                                : 'none',
                            '&:hover': {
                                backgroundColor: location.pathname === '/notifications'
                                    ? alpha(theme.palette.common.white, 0.08)
                                    : alpha(theme.palette.common.white, 0.08)
                            },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: !isCollapsed ? 'center' : 'flex-start',
                            gap: !isCollapsed ? 0.5 : 1,
                            width: '100%'
                        }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                minWidth: 0,
                                flexDirection: !isCollapsed ? 'column' : 'row',
                                gap: !isCollapsed ? 0.5 : 1
                            }, children: [_jsx(ListItemIcon, { sx: {
                                        minWidth: 0,
                                        justifyContent: 'center',
                                        color: location.pathname === '/notifications'
                                            ? activeItemColor || theme.palette.common.white
                                            : textColor || theme.palette.grey[400]
                                    }, children: _jsx(Badge, { badgeContent: notificationsBadgeCount, max: 99, overlap: 'circular', invisible: !notificationsBadgeCount || isCollapsed, sx: {
                                            '& .MuiBadge-badge': {
                                                backgroundColor: companyTheme?.primaryColor || theme.palette.primary.main,
                                                color: companyTheme?.primaryColor
                                                    ? getContrastText(companyTheme.primaryColor)
                                                    : theme.palette.primary.contrastText
                                            }
                                        }, children: _jsx(NotificationBing, { variant: 'Outline' }) }) }), isCollapsed && (_jsxs(_Fragment, { children: [_jsx(ListItemText, { primary: t('notifications.title') || 'Notifications', primaryTypographyProps: {
                                                noWrap: true,
                                                sx: {
                                                    flex: 1,
                                                    minWidth: 0,
                                                    width: '100%',
                                                    maxWidth: '100%',
                                                    fontSize: !isCollapsed ? '10px' : '14px',
                                                    color: location.pathname === '/notifications'
                                                        ? activeItemColor || theme.palette.common.white
                                                        : textColor || theme.palette.grey[400],
                                                    fontWeight: location.pathname === '/notifications' ? 600 : 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'block'
                                                }
                                            }, sx: {
                                                textAlign: !isCollapsed ? 'center' : 'left',
                                                minWidth: 0,
                                                flex: 1,
                                                width: '100%'
                                            } }), notificationsBadgeCount > 0 && (_jsx(Chip, { label: notificationsBadgeCount > 99 ? '99+' : notificationsBadgeCount, size: 'small', color: 'primary', sx: {
                                                height: 20,
                                                minWidth: 20,
                                                borderRadius: 10,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                marginLeft: 1
                                            } }))] }))] }) }), _jsx(Box, { sx: {
                            position: 'relative',
                            mb: 2,
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '1px',
                                background: 'linear-gradient(90deg, rgba(0, 182, 237, 0.5) 0%, rgba(0, 182, 237, 0) 100%)'
                            }
                        } }), _jsx(Box, { sx: { marginBottom: isCollapsed ? 0 : theme.spacing(3) }, children: isCollapsed ? (_jsx(StyledSearchInput, { fullWidth: true, bordered: false, 
                            // value={searchValue || ''}
                            // onChange={(e) => onSearch?.(e.target.value)}
                            onClick: () => {
                                localStorage.setItem('globalSearchAutoFocus', 'true');
                                navigate('/global-search');
                            }, sx: {
                                cursor: 'pointer',
                                '& .MuiInputBase-root': { backgroundColor: 'transparent', height: '40px' }
                            }, readOnly: true })) : (_jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', children: _jsx(IconButton, { sx: { backgroundColor: theme.palette.background.paper }, onClick: () => {
                                    localStorage.setItem('globalSearchAutoFocus', 'true');
                                    navigate('/global-search');
                                }, children: _jsx(SearchNormal, { variant: 'Outline', color: theme.palette.grey[400] }) }) })) }), _jsx(Box, { sx: { flex: 1, overflowY: 'auto', overflowX: 'hidden' }, children: filteredNavigationSections.map((section, sectionIndex) => (_jsxs(Box, { children: [isCollapsed && section.title && (_jsx(Typography, { sx: {
                                        padding: theme.spacing(2, 1.5),
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        color: theme.palette.grey[500],
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }, children: section.title })), _jsx(List, { disablePadding: true, sx: { padding: theme.spacing(0, 0) }, children: section.items.map((item) => renderNavigationItem(item)) })] }, sectionIndex))) }), latestManual?.versionLabel && (_jsx(Typography, { variant: 'caption', sx: {
                            flexShrink: 0,
                            display: 'block',
                            width: '100%',
                            textAlign: 'center',
                            color: theme.palette.grey[400],
                            pt: 2,
                            pb: 1
                        }, children: t('common.appVersion', { version: latestManual.versionLabel }) }))] }), _jsx(UserMenu, { anchorEl: userMenuAnchor, open: Boolean(userMenuAnchor), companies: companies, selectedCompanyId: selectedCompanyId[0] ?? undefined, onCompanySelect: onCompanySelect, onClose: handleUserMenuClose, onMyAccount: handleMyAccount, onGroupChange: handleGroupChange, onLogout: handleLogout }), _jsx(EventMenu, { anchorEl: eventMenuAnchor, open: Boolean(eventMenuAnchor), onClose: onCloseEventMenu, onAllEvents: onAllEventsClick, onCreateEvent: onCreateEventsClick }), _jsxs(Menu, { anchorEl: companyMenuAnchor, open: Boolean(companyMenuAnchor), onClose: () => {
                    setCompanyMenuAnchor(null);
                    setCompanySearchTerm('');
                }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: {
                    sx: {
                        width: 420,
                        borderRadius: 2,
                        mt: 1,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        maxHeight: 500,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }
                }, MenuListProps: {
                    sx: {
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        height: '100%'
                    }
                }, children: [_jsx(ListSubheader, { sx: {
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme.palette.grey[500],
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            lineHeight: '32px',
                            backgroundColor: theme.palette.background.paper,
                            zIndex: 1,
                            flexShrink: 0
                        }, children: t('common.selectGroup') }), _jsxs(Box, { sx: {
                            px: 2,
                            pb: 1,
                            pt: 1,
                            backgroundColor: theme.palette.background.paper,
                            zIndex: 1,
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                        }, children: [_jsx(TextField, { fullWidth: true, placeholder: t('common.SearchCompanies...'), variant: 'outlined', size: 'small', value: companySearchTerm, onChange: (e) => {
                                    e.stopPropagation();
                                    setCompanySearchTerm(e.target.value);
                                }, onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), autoComplete: 'off', inputProps: {
                                    onBlur: (e) => {
                                        e.preventDefault();
                                        e.target.focus();
                                    }
                                } }), filteredCompaniesForMenu.length > 0 && (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }, onClick: () => {
                                    const filteredCompanyIds = filteredCompaniesForMenu.map((c) => c.id);
                                    const allFilteredSelected = filteredCompanyIds.every((id) => selectedCompanyId.includes(id));
                                    if (allFilteredSelected) {
                                        const updatedSelection = selectedCompanyId.filter((id) => !filteredCompanyIds.includes(id));
                                        dispatch(setSelectedCompanyId(updatedSelection));
                                        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
                                    }
                                    else {
                                        const merged = [...selectedCompanyId, ...filteredCompanyIds];
                                        const updatedSelection = Array.from(new Set(merged));
                                        dispatch(setSelectedCompanyId(updatedSelection));
                                        LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
                                    }
                                    dispatch(refreshTokenAsync());
                                    dispatch(getAllPeopleForTasksAsync());
                                }, children: [_jsx(Checkbox, { checked: filteredCompaniesForMenu.every((c) => selectedCompanyId.includes(c.id)), size: 'small', onClick: (e) => {
                                            e.stopPropagation();
                                            const filteredCompanyIds = filteredCompaniesForMenu.map((c) => c.id);
                                            const allFilteredSelected = filteredCompanyIds.every((id) => selectedCompanyId.includes(id));
                                            if (allFilteredSelected) {
                                                const updatedSelection = selectedCompanyId.filter((id) => !filteredCompanyIds.includes(id));
                                                dispatch(setSelectedCompanyId(updatedSelection));
                                                LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
                                            }
                                            else {
                                                const merged = [...selectedCompanyId, ...filteredCompanyIds];
                                                const updatedSelection = Array.from(new Set(merged));
                                                dispatch(setSelectedCompanyId(updatedSelection));
                                                LocalStorageService.setSelectedCompanyIdForTab(updatedSelection);
                                            }
                                            dispatch(refreshTokenAsync());
                                            dispatch(getAllPeopleForTasksAsync());
                                        } }), _jsx(Typography, { variant: 'caption', sx: { fontSize: '12px', whiteSpace: 'nowrap' }, children: companySearchTerm
                                            ? `${t('actions.selectAll') || 'Select All'} (${filteredCompaniesForMenu.length})`
                                            : t('actions.selectAll') || 'Select All' })] }))] }), _jsx(Box, { sx: {
                            overflowY: 'auto',
                            flexGrow: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            position: 'relative'
                        }, children: renderCompanyMenuItems() })] })] }));
};
export default Sidebar;
